import { ArrowUpRight, MoreVertical, ArrowRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import get from "lodash/get";
import useAppContext from "../../../../context/useAppContext";
import { getExpressionValue, buildExpressionPayload } from "./utils/expressionUtils";
import {
  FieldMappingCreationAndEditDict,
  FieldMappingInstance,
  FieldMappingOptionsResponse,
  FieldMappingTarget,
  LinkedAccount,
} from "../../../../../models/Entities";
import DeleteFieldMappingModal from "./DeleteFieldMappingModal";
import {
  createFieldMappingOptions,
  parseFieldTraversalPath,
} from "../../../configuration/field-mappings/utils/FieldMappingUtils";
import {
  EditFieldMappingInstanceProps,
  NewFieldMappingProps,
  createLinkedAccountFieldMapping,
  editFieldMappingInstance,
  fetchFieldMappingInstance,
} from "../../../../../api-client/APIClient";
import { FieldMappingSource } from "../../../../../constants";
import {
  Typeahead,
  Button,
  ButtonVariant,
  Menu,
  MenuItem,
} from "@merge-api/merge-javascript-shared";
import useLinkedAccountCustomMappingsContext from "./context/useLinkedAccountFieldMappingsContext";
import FieldMappingDropdownChild from "../../../../shared/FieldMappingDropdownChild";
import { showSuccessToast, showErrorToast } from "../../../../shared/Toasts";
import FieldMappingsGridContainer from "./components/FieldMappingsGridContainer";
import FieldMappingCard from "./components/FieldMappingCard";
import FieldMappingTargetBadge from "../../../../shared/FieldMappingTargetBadge";
import { navigateToFieldMappingSettings } from "../../../../../router/RouterUtils";
import { useHistory } from "react-router-dom";
import FieldMappingCardRemoteFieldsSubtitle from "./components/FieldMappingCardRemoteFieldsSubtitle";
import FieldMappingRemoteFieldsBadge from "./components/FieldMappingRemoteFieldsBadge";
import EditJmesPathModal from "./modal/steps/advanced-field-mapping/components/EditJsonAdvancedMappingExpressionModal";
import { useRemoteFieldOptionFilter } from "./utils/remoteFieldSearch";

type Props = {
  fieldMapping: FieldMappingInstance;
  linkedAccount: LinkedAccount;
  fieldMappingMetaResponse: FieldMappingOptionsResponse | undefined;
  common_model: string;
  newMapping?: boolean;
  newMappingSaved?: (instance: FieldMappingInstance) => void;
  cancelNewMapping?: () => void;
  exampleValue?: string | null;
};

const FieldMappingInstanceRow = ({
  fieldMapping: fieldMapping,
  linkedAccount,
  fieldMappingMetaResponse,
  common_model,
  newMapping = false,
  newMappingSaved = () => {},
  exampleValue,
}: Props) => {
  const history = useHistory();
  const { user } = useAppContext();
  const isJmesPathDeprecationEnabled = user?.is_jmes_path_deprecation_dashboard_enabled ?? false;
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [creatingNewMapping, setCreatingNewMapping] = useState<boolean>(newMapping);
  const { refreshFieldMappings: refreshCustomMappings } = useLinkedAccountCustomMappingsContext();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingAdvancedMappingPath, setEditAdvancedMappingPath] = useState(false);

  const [fieldMappingOptions, setFieldMappingOptions] = useState<FieldMappingCreationAndEditDict[]>(
    [],
  );
  const filterRemoteFields = useRemoteFieldOptionFilter(fieldMappingOptions);
  const [originFields, setOriginFields] = useState<FieldMappingCreationAndEditDict | null>(null);
  const [fieldMappingTarget, setFieldMappingTarget] = useState<FieldMappingTarget | null>(null);
  const [fieldMappingInstance, setFieldMappingInstance] =
    useState<FieldMappingInstance>(fieldMapping);
  const fieldMappingMetaOptions = fieldMappingMetaResponse?.remote_field_options;

  const fieldMappingTargetID =
    fieldMappingInstance.is_integration_wide && fieldMappingInstance.field_mapping_target !== null
      ? fieldMappingInstance.field_mapping_target.id
      : undefined;

  const [fieldMappingInstanceID, setFieldMappingInstanceID] = useState<string | undefined>(
    fieldMapping.is_integration_wide && fieldMapping.field_mapping_target !== null
      ? undefined
      : fieldMapping.id,
  );

  const jsonAdvancedMappingExpression = getExpressionValue(
    fieldMappingInstance,
    isJmesPathDeprecationEnabled,
  );
  const remoteFieldType = get(originFields, "type", "");
  const isAdvancedMappingType = remoteFieldType.includes("list") || remoteFieldType == "dict";
  const hasOptionChanged =
    parseFieldTraversalPath(get(originFields, "traversal_path", [])).toString() !==
    fieldMappingInstance.field_traversal_path.toString();

  const updateFieldMappingTarget = () => {
    if (fieldMappingTargetID && fieldMappingTarget == null) {
      setFieldMappingTarget(fieldMappingInstance.field_mapping_target ?? null);
    }
  };
  updateFieldMappingTarget();

  const fetchAndSetFieldMappingInstanceInfo = (onResponse: () => void) => {
    if (fieldMappingInstanceID && fieldMappingInstance == null) {
      fetchFieldMappingInstance(fieldMappingInstanceID, setFieldMappingInstance);
    }

    onResponse();
  };
  fetchAndSetFieldMappingInstanceInfo(() => {});

  useEffect(() => {
    if (creatingNewMapping == false && newMapping) {
      newMappingSaved(fieldMappingInstance);
    }
  }, [fieldMappingInstance]);

  const setandFetchFieldMappingInstance = (id: string) => {
    setFieldMappingInstanceID(id);
    setCreatingNewMapping(false);
    fetchFieldMappingInstance(id, setFieldMappingInstance);
  };

  useEffect(() => {
    if (fieldMappingMetaOptions !== undefined) {
      setFieldMappingOptions(createFieldMappingOptions(fieldMappingMetaOptions, common_model));
    }
  }, [fieldMappingMetaOptions]);

  const createFieldMapping = (
    passedOrigin: FieldMappingCreationAndEditDict | undefined = undefined,
    jsonAdvancedMappingExpression: string | null = null,
  ) => {
    const origin = passedOrigin ? passedOrigin : originFields;
    if (origin) {
      const body: NewFieldMappingProps = {
        linked_account_id: linkedAccount.id,
        common_model_id: `${linkedAccount.category}.${common_model}`,
        field_key: fieldMappingTarget?.field_key ?? "",
        field_traversal_path: parseFieldTraversalPath(origin.traversal_path),
        create_for_organization: false,
        configured_by: FieldMappingSource.ORGANIZATION,
        ...(fieldMappingTarget?.field_description
          ? {
              field_description: fieldMappingTarget?.field_description,
            }
          : {}),
        ...(fieldMappingTargetID ? { field_mapping_target_id: fieldMappingTargetID } : {}),
        api_endpoint_id: origin.api_endpoint_id,
        display_name: origin.display_name,
        origin_type: origin.type,
        ...buildExpressionPayload(jsonAdvancedMappingExpression, isJmesPathDeprecationEnabled),
      };
      createLinkedAccountFieldMapping(
        body,
        (createdFieldMappingId: string) => {
          setandFetchFieldMappingInstance(createdFieldMappingId);
          showSuccessToast("Successfully created mapping!");
          setIsEditing(false);
        },
        async (response) => {
          setIsEditing(false);
          if (response) {
            try {
              const errorData = await response.json();
              if (errorData?.advanced_mapping_expression?.[0]) {
                showErrorToast(
                  `Invalid advanced mapping expression: ${errorData.advanced_mapping_expression[0]}`,
                );
              } else if (errorData?.jmes_path?.[0]) {
                showErrorToast(`Invalid JMESPath expression: ${errorData.jmes_path[0]}`);
              } else {
                showErrorToast("Unable to create field mapping");
              }
            } catch {
              showErrorToast("Unable to create field mapping");
            }
          } else {
            showErrorToast("Unable to create field mapping");
          }
        },
      );
    }
  };

  const fetchAndUpdateFieldMappingInstance = () => {
    fetchFieldMappingInstance(fieldMappingInstanceID!, setFieldMappingInstance, () => {
      setIsEditing(false);
    });
  };

  const updateCustomMappingInstance = (originFields: any, jmes_path: string | null = null) => {
    if (fieldMappingInstanceID && originFields) {
      const expressionValue =
        jmes_path ?? getExpressionValue(originFields, isJmesPathDeprecationEnabled);
      const body: EditFieldMappingInstanceProps = {
        field_traversal_path: parseFieldTraversalPath(originFields.traversal_path),
        field_mapping_instance_id: fieldMappingInstanceID,
        api_endpoint_id: originFields.api_endpoint_id,
        origin_type: originFields.type,
        display_name: originFields.display_name,
        field_description: fieldMappingInstance?.field_description,
        ...buildExpressionPayload(expressionValue, isJmesPathDeprecationEnabled),
      };
      editFieldMappingInstance(fieldMappingInstanceID!, body, () => {
        fetchAndUpdateFieldMappingInstance();
        setIsEditing(false);
        showSuccessToast("Successfully edited mapping!");
      });
    }
  };

  useEffect(() => {
    if (fieldMappingInstance && fieldMappingOptions) {
      const currentOption = fieldMappingOptions.find(
        (fieldMappingOption) =>
          fieldMappingOption.display_name === fieldMappingInstance.display_name,
      );
      if (currentOption) {
        setOriginFields(currentOption);
      }
    }
  }, [fieldMappingInstance, fieldMappingOptions, isEditing]);

  useEffect(() => {
    if (fieldMappingMetaOptions !== undefined) {
      setFieldMappingOptions(createFieldMappingOptions(fieldMappingMetaOptions, common_model));
    }
  }, [fieldMappingMetaOptions]);

  // The dropdown logic below was a little complicated but basically:
  // 1. Render dropdown only if the field mapping is not int-wide OR it has linked account level overrides, and
  // if it's not the case that it's integration wide and remote data/shallow runs arent enabled.
  // 2. Render edit only if remote data was enabled. The edit page will either route to a linked account edit
  // if the rendered instance is a non int wide mapping or an override if its an int wide mapping
  // 3. Only show delete if it's not an integration wide mapping. cant delete from this page

  const onDeleteSuccess = (data: any) => {
    showSuccessToast("Successfully deleted Field Mapping");
    const orgWideInstance = get(data, "org_wide_instance", null);
    if (orgWideInstance) {
      // deleted linked account override set field mapping to the org wide instance
      setFieldMappingInstance(orgWideInstance);
    } else {
      refreshCustomMappings();
    }
  };

  return (
    <>
      {editingAdvancedMappingPath &&
        (isJmesPathDeprecationEnabled || isAdvancedMappingType) &&
        originFields?.value && (
          <EditJmesPathModal
            currentJsonAdvancedMappingExpression={
              hasOptionChanged ? null : jsonAdvancedMappingExpression
            }
            jsonData={originFields.value}
            remoteFieldDisplayName={fieldMappingInstance.display_name}
            onModalClose={() => {
              setEditAdvancedMappingPath(false);
            }}
            onUpdate={(expression: string) => {
              if (fieldMappingInstance.is_integration_wide) {
                createFieldMapping(originFields, expression);
              } else {
                updateCustomMappingInstance(originFields, expression);
              }
              setEditAdvancedMappingPath(false);
            }}
            linkedAccount={linkedAccount}
          />
        )}

      <DeleteFieldMappingModal
        fieldMappingID={fieldMappingInstance.id}
        showModal={showDeleteModal}
        linkedAccountID={linkedAccount.id}
        onHide={() => setShowDeleteModal(false)}
        onSuccess={onDeleteSuccess}
        skipFieldMappingRefresh={fieldMappingInstance?.field_mapping_target != null}
      />
      <FieldMappingsGridContainer hasDropdown>
        <>
          {isEditing ? (
            <div className="flex flex-col w-100 gap-y-3">
              <Typeahead
                value={fieldMappingOptions.find(
                  (fieldMappingOption) =>
                    fieldMappingOption.display_name === fieldMappingInstance.display_name,
                )}
                options={fieldMappingOptions}
                onChange={(_, selectedRemoteField: any) => {
                  setOriginFields(selectedRemoteField);
                }}
                placeholder={
                  fieldMappingOptions.length > 0
                    ? "Search fields by name or value..."
                    : "No fields found."
                }
                getOptionLabel={(option: FieldMappingCreationAndEditDict) => {
                  return option?.display_name ?? "";
                }}
                filterOptions={(_options, { inputValue }) => filterRemoteFields(inputValue)}
                renderOption={(option: FieldMappingCreationAndEditDict) => {
                  return <FieldMappingDropdownChild option={option} />;
                }}
                loadingText="Loading fields..."
                loading={fieldMappingMetaOptions == undefined}
              />
              {(isJmesPathDeprecationEnabled || isAdvancedMappingType) && (
                <>
                  {hasOptionChanged ? (
                    <Button
                      size="sm"
                      variant={ButtonVariant.SecondaryGray}
                      onClick={() => {
                        setEditAdvancedMappingPath(true);
                      }}
                      className="ml-auto"
                      rightIcon={<ArrowUpRight size={12} />}
                    >
                      Advanced mapping
                    </Button>
                  ) : (
                    <div className="flex items-center py-2 px-3  justify-between bg-blue-0 rounded-md text-sm">
                      <div className="text-wrap">{jsonAdvancedMappingExpression}</div>
                      <Button
                        variant={ButtonVariant.TextBlue}
                        size="sm"
                        rightIcon={<ArrowUpRight size={12} />}
                        onClick={() => {
                          setEditAdvancedMappingPath(true);
                        }}
                      >
                        Advanced mapping
                      </Button>
                    </div>
                  )}
                </>
              )}
              <div className="flex flex-row items-center justify-content-end gap-x-3">
                <Button
                  size="sm"
                  variant={ButtonVariant.TextBlack}
                  onClick={() => {
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant={ButtonVariant.TertiaryWhite}
                  disabled={originFields ? false : true}
                  onClick={() => {
                    if (originFields) {
                      // if we are creating a linked account override for an integration wide field mapping we should be creating a new linked account field mapping instance
                      if (fieldMappingInstance?.is_integration_wide) {
                        createFieldMapping(originFields);
                      } else {
                        updateCustomMappingInstance(originFields);
                      }
                    }
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <FieldMappingCard
              displayName={fieldMappingInstance.display_name}
              description={
                <FieldMappingCardRemoteFieldsSubtitle
                  exampleValue={exampleValue}
                  jsonAdvancedMappingExpression={jsonAdvancedMappingExpression}
                  remoteEndpointMethod={fieldMappingInstance.remote_endpoint_method}
                  remoteEndpointPath={fieldMappingInstance.remote_endpoint_path}
                />
              }
              badge={
                <FieldMappingRemoteFieldsBadge
                  isOrganizationWide={fieldMappingInstance?.is_integration_wide}
                  linkedAccount={linkedAccount}
                />
              }
              type={fieldMappingInstance.origin_type}
            />
          )}
          <div className="flex justify-center items-center h-full">
            <ArrowRight size={20} />
          </div>
          <FieldMappingCard
            displayName={fieldMappingInstance.field_key}
            description={fieldMappingInstance.field_description}
            badge={
              <FieldMappingTargetBadge
                type={
                  fieldMappingInstance?.field_mapping_target == null
                    ? "linked-account-specific-target-field"
                    : "org-wide-target-field"
                }
                linkedAccount={linkedAccount}
              />
            }
            onClick={
              fieldMappingInstance.is_integration_wide
                ? () =>
                    navigateToFieldMappingSettings(
                      history,
                      fieldMappingInstance?.field_mapping_target?.id,
                    )
                : undefined
            }
          />
          <div className="flex items-center mx-1">
            <Menu
              ButtonProps={{
                children: <MoreVertical size={16} />,
                variant: ButtonVariant.IconShadowHidden,
                className: "p-0",
                size: "sm",
              }}
              menuPlacement="bottom-end"
            >
              <MenuItem onClick={() => setIsEditing(true)}>Edit mapping</MenuItem>
              {!fieldMappingInstance.is_integration_wide && (
                <MenuItem
                  onClick={() => setShowDeleteModal(true)}
                  className={fieldMappingInstance.is_integration_wide ? "text-gray-40" : ""}
                >
                  Delete mapping
                </MenuItem>
              )}
            </Menu>
          </div>
        </>
      </FieldMappingsGridContainer>
    </>
  );
};

export default FieldMappingInstanceRow;
